import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { Marker as ReactMarker, useMap } from 'react-leaflet';

import {
  useAppSelector,
  labelsSelector,
  labelPositionsSelector,
  isEditingSelector,
  editModeSelector,
  showLabelsSelector,
  DEFAULT_LABEL_STYLE,
  DEFAULT_LABEL_GROUP_COLOR,
  LabelSelectionGroup,
  preferencesSelector,
} from '../../../../store';

import { LabelEditing } from './LabelEditing';
import { Marker } from './Marker';
import eventBus from '../../../../eventBus';

const GROUP_LABEL_ZOOM_FRACTION = 0.5;
const INDIVIDUAL_LABEL_REVEAL_ZOOM_OFFSET = 0.75;
const MAX_ZOOM_RANGE_FOR_GROUPING = 5;

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeHexColor(value: string | undefined) {
  if (!value) return DEFAULT_LABEL_GROUP_COLOR;

  const trimmed = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    return `#${trimmed.slice(1).split('').map((char) => `${char}${char}`).join('')}`;
  }

  return DEFAULT_LABEL_GROUP_COLOR;
}

function hexToRgb(value: string) {
  const normalized = normalizeHexColor(value).slice(1);
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function contrastTextColor(value: string) {
  const { r, g, b } = hexToRgb(value);
  const luminance = ((0.299 * r) + (0.587 * g) + (0.114 * b)) / 255;
  return luminance > 0.58 ? '#0f172a' : '#f8fafc';
}

function getGroupLabelZoomThreshold(map: L.Map) {
  const minZoom = map.getMinZoom();
  const maxZoom = map.getMaxZoom();

  if (!Number.isFinite(minZoom) || !Number.isFinite(maxZoom) || maxZoom <= minZoom) {
    return minZoom;
  }

  // Cap the range so the threshold stays close to maxZoom on wide-range maps (e.g. external layers)
  const effectiveMinZoom = Math.max(minZoom, maxZoom - MAX_ZOOM_RANGE_FOR_GROUPING);
  return effectiveMinZoom + ((maxZoom - effectiveMinZoom) * GROUP_LABEL_ZOOM_FRACTION);
}

function getIndividualLabelZoom(map: L.Map) {
  const maxZoom = map.getMaxZoom();
  const threshold = getGroupLabelZoomThreshold(map);
  const zoom = Math.min(maxZoom, threshold + INDIVIDUAL_LABEL_REVEAL_ZOOM_OFFSET);

  return Number.isFinite(zoom) ? zoom : threshold;
}

function makeLabelGroupIcon(groupName: string, color?: string): L.DivIcon {
  const escapedCaption = escapeHtml(groupName);
  const estimatedWidth = Math.min(220, Math.max(82, groupName.length * 8 + 48));
  const markerColor = normalizeHexColor(color);
  const { r, g, b } = hexToRgb(markerColor);
  const textColor = contrastTextColor(markerColor);
  const html = `<div
    data-label-group='${escapedCaption}'
    data-label-group-color='${markerColor}'
    title='${escapedCaption} label group'
    style="
      display:inline-flex;
      align-items:center;
      justify-content:center;
      min-width:58px;
      max-width:220px;
      height:34px;
      padding:0 14px;
      border-radius:999px;
      background:rgba(${r},${g},${b},0.94);
      border:1px solid rgba(${r},${g},${b},0.72);
      box-shadow:0 8px 24px rgba(15,23,42,0.35);
      color:${textColor};
      font-size:13px;
      font-weight:700;
      line-height:1;
      white-space:nowrap;
      backdrop-filter:blur(6px);
      overflow:hidden;
    "
  >
    <span style="overflow:hidden;text-overflow:ellipsis;line-height: 34px;">${escapedCaption}</span>
  </div>`;

  return L.divIcon({
    html,
    className: 'label-cluster-icon',
    iconSize: [estimatedWidth, 34],
    iconAnchor: [estimatedWidth / 2, 17],
  });
}

export function Labels() {
  const isEditing = useAppSelector(isEditingSelector);
  const editMode = useAppSelector(editModeSelector);
  const labels = useAppSelector(labelsSelector);
  const labelPositions = useAppSelector(labelPositionsSelector);
  const preferences = useAppSelector(preferencesSelector);

  const selectionGestureActiveRef = useRef(false);
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());

  useEffect(() => {
    const updateZoom = () => setZoom(map.getZoom());
    updateZoom();
    map.on('zoomend', updateZoom);
    return () => {
      map.off('zoomend', updateZoom);
    };
  }, [map]);

  useEffect(() => {
    const handleSelectionGestureStarted = () => {
      selectionGestureActiveRef.current = true;
    };

    const handleSelectionGestureEnded = () => {
      selectionGestureActiveRef.current = false;
    };

    eventBus.on('label-selection-gesture-started', handleSelectionGestureStarted);
    eventBus.on('label-selection-gesture-ended', handleSelectionGestureEnded);

    return () => {
      eventBus.off('label-selection-gesture-started', handleSelectionGestureStarted);
      eventBus.off('label-selection-gesture-ended', handleSelectionGestureEnded);
    };
  }, []);

  const showLabels = useAppSelector(showLabelsSelector);
  if (!showLabels) return null;

  const visibleGroupMarkers = zoom <= getGroupLabelZoomThreshold(map);
  const visibleLabels = labels.filter((label) => {
    const pos = labelPositions[label.id];
    if (!pos) return false;
    if (visibleGroupMarkers) {
      const inGroup = (preferences.labelSelectionGroups ?? [])
        .some((group) => group.labelIds.includes(label.id));
      return !inGroup;
    }
    return true;
  });

  const groupedLabelIds = new Set<string>();
  const GroupMarkers = (preferences.labelSelectionGroups ?? []).map((group: LabelSelectionGroup) => {
    const groupName = group.name.trim();
    if (!groupName) return null;

    const groupLabelPositions = [...new Set(group.labelIds)].map((labelId) => {
      if (!labelPositions[labelId]) return { labelId, position: null };
      const { x, y } = labelPositions[labelId];
      const position = { x: Number(x), y: Number(y) };
      return { labelId, position };
    }).filter((entry): entry is { labelId: string; position: { x: number; y: number } } =>
      entry.position !== null && Number.isFinite(entry.position.x) && Number.isFinite(entry.position.y));

    if (groupLabelPositions.length < 2) return null;

    groupLabelPositions.forEach(({ labelId }) => groupedLabelIds.add(labelId));
    const groupPosition = groupLabelPositions.reduce((result, { position }) => {
      return {
        x: result.x + position.x,
        y: result.y + position.y,
      };
    }, { x: 0, y: 0 });
    const position = [
      groupPosition.x / groupLabelPositions.length,
      groupPosition.y / groupLabelPositions.length,
    ] as [number, number];

    return (
      <ReactMarker
        key={`label-group-${groupName}`}
        position={position}
        icon={makeLabelGroupIcon(groupName, group.color)}
        zIndexOffset={DEFAULT_LABEL_STYLE.zIndex + 10}
        bubblingMouseEvents={false}
        eventHandlers={{
          click: (e) => {
            L.DomEvent.stopPropagation(e);
            map.flyTo(position, getIndividualLabelZoom(map));
          },
        }}
      />
    );
  }).filter(Boolean);

  if (isEditing && editMode === 'label') {
    return <LabelEditing />;
  }

  return (
    <>
      {visibleGroupMarkers && GroupMarkers}
      {visibleLabels.map((label) => (
        <Marker
          key={label.id}
          label={label}
          position={labelPositions[label.id]}
          defaultRotation={preferences.defaultLabelRotation}
        />
      ))}
    </>
  );
}
